import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useState } from "react";

export default function PromoCarousel({ promotions }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!promotions || promotions.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? promotions.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === promotions.length - 1 ? 0 : prev + 1));
  };

  const handlePromoClick = (promo) => {
    if (promo.link_url) {
      window.open(promo.link_url, "_blank");
    }
  };

  return (
    <div className="mb-6">
      <div className="relative">
        {/* Main Carousel */}
        <div className="overflow-hidden rounded-xl">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {promotions.map((promo) => (
              <div
                key={promo.id}
                className="w-full flex-shrink-0 cursor-pointer"
                onClick={() => handlePromoClick(promo)}
              >
                <div className="relative h-36 sm:h-44 md:h-52 rounded-xl overflow-hidden">
                  {/* Background Image or Gradient */}
                  {promo.image_base64 ? (
                    <img
                      src={promo.image_base64}
                      alt={promo.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-[#1aa59a] to-[#158a80]" />
                  )}
                  
                  {/* Dark Overlay for Text Readability */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
                  
                  {/* Text Content - Always Visible */}
                  <div className="absolute inset-0 flex flex-col justify-center px-4 sm:px-6">
                    <h3 className="text-white font-bold text-lg sm:text-xl md:text-2xl drop-shadow-lg mb-1">
                      {promo.title || "Special Offer"}
                    </h3>
                    {promo.subtitle && (
                      <p className="text-white/90 text-sm sm:text-base drop-shadow-md max-w-[70%]">
                        {promo.subtitle}
                      </p>
                    )}
                    {promo.link_url && (
                      <div className="flex items-center gap-1 text-white/80 text-xs mt-2">
                        <ExternalLink className="w-3 h-3" />
                        <span>Tap to learn more</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Arrows */}
        {promotions.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all"
              data-testid="promo-prev"
            >
              <ChevronLeft className="w-4 h-4 text-gray-800" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all"
              data-testid="promo-next"
            >
              <ChevronRight className="w-4 h-4 text-gray-800" />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {promotions.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {promotions.map((promo, index) => (
              <button
                key={promo.id || `promo-${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentIndex ? "bg-[#1aa59a] w-5" : "bg-gray-300 w-1.5"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
